/**
 * This custom hook provides the functionality to retrieve users to be shown in the UI.
 */

import React, {useContext, useState} from "react";
import {FirebaseAuthContext} from "../../../../commons/firebase/FirebaseAuthProvider";
import {useSnackbar} from "notistack";
import {useIntl} from "react-intl";
import {useConfirm} from "material-ui-confirm";
import VideoCard from "../card/VideoCard";
import {Visibility} from "@mui/icons-material";
import {useStyles} from "./GeneralEditVideoForm.css";

const useCheckVideoDuplicate = () => {
    const {signedInUser} = useContext(FirebaseAuthContext);

    const {enqueueSnackbar} = useSnackbar();
    const confirm = useConfirm();

    const classes = useStyles()

    // Internationalization for simple strings
    const intl = useIntl();

    const [checkInProgress, setCheckInProgress] = useState(false)

    const handleSaveDuplicate = async (videoData, onNonDuplicateOrConfirm) => {
        setCheckInProgress(true)
        const idToken = await signedInUser.getIdToken()

        return fetch('/reg-main/videos/functions/potentiallyDuplicateVideos' +
            '?idToken=' + idToken +
            '&key=' + videoData.key +
            '&association=' + videoData.association +
            '&discipline=' + videoData.discipline +
            '&bout=' + videoData.bout +
            '&tournament=' + videoData.tournament +
            '&year=' + videoData.year +
            '&ageGroup=' + videoData.ageGroup +
            '&surnameAthlete1=' + videoData.surnameAthlete1 +
            '&surnameAthlete2=' + videoData.surnameAthlete2 +
            '&gender=' + videoData.gender)
            .then(async (data) => {
                // Throw an error including the textual status of the HTTP response code (no detailed message necessary)
                if (data.status !== 200) {
                    throw new Error((await data.json()).status)
                }

                return data.json();
            })
            .then((duplicateVideos) => {
                setCheckInProgress(false)
                if (duplicateVideos?.length > 0) {
                    return confirm({
                        title: intl.formatMessage({
                            id: 'app.videos.dialog.duplicationCheck.confirm.title',
                            defaultMessage: 'Potential Video Duplicate'
                        }),
                        content: <div>
                            <div>
                                {intl.formatMessage({
                                    id: 'app.videos.dialog.duplicationCheck.confirm.description',
                                    defaultMessage: 'Videos with the same input is already existing - save it anyway?'
                                })}
                            </div>
                            <div>
                                {duplicateVideos.map((duplicateVideo) => {
                                    return (<div key={duplicateVideo.key} className={classes.duplicateVideoRow}>
                                        <VideoCard video={duplicateVideo}/>
                                        <a href={duplicateVideo.vimeo.accessLink} target="_blank" rel="noopener noreferrer">
                                            <Visibility/>
                                        </a>
                                    </div>)
                                })}
                            </div>
                        </div>
                    })
                        .then(() => 'OK')
                        .catch(() => 'CANCEL')
                } else {
                    return 'OK'
                }
            })
            .then((saveAllowed) => {
                if (saveAllowed === 'OK') {
                    return onNonDuplicateOrConfirm()
                } else {
                    return null
                }
            })
            .catch((error) => {
                enqueueSnackbar(
                    intl.formatMessage({
                        id: 'app.videos.messages.duplicationCheck.error',
                        defaultMessage: 'Something went wrong while checking the video for duplication: {error}'
                    }, {error: error.message}), {
                        variant: 'error',
                        preventDuplicate: true,
                        autoHideDuration: 5000
                    });
            })
            .finally(() => {
                setCheckInProgress(false)
            })
    }

    return {
        handleSaveDuplicate,
        checkInProgress
    }
}
export default useCheckVideoDuplicate;